import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Message } from "@ag-ui/client";
import { ChevronDown } from "lucide-react";
import useToast from "~/components/ui/toast";
import type { AgentConnector, AgentStarterPrompt, PartialAgent } from "~/services/agents";
import {
  cancelConversation,
  type ConversationConnect,
  conversationConnectPath,
} from "~/services/conversations";
import type { PartialSkill } from "~/services/skills";
import { apiClient } from "~/utils/apiClient";
import { ConversationTurns } from "./ConversationTurns";
import { EmptyConversationView } from "./EmptyConversationView";
import { McpAppEntriesProvider } from "./McpAppEntriesContext";
import type { ReplayHttpAgent } from "./ReplayHttpAgent";
import { ToolUIProvider } from "./ToolUIContext";
import { danglingToolCalls, hitlPauseIds, unansweredHitlCallIds } from "./awaitingInput";
import { Composer } from "./composer/Composer";
import type { McpGuidelines } from "./connectors/useMcpGuidelines";
import { parseContextBuffer } from "./contextBuffer";
import { TOOL_UI_REGISTRY } from "./tool-ui-registry";
import { useAgent } from "./useAgent";
import { BOTTOM_THRESHOLD, useAnchoredScroll } from "./useAnchoredScroll";
import { useInitializationPanelSync } from "./useInitializationPanelSync";
import { useMcpAppEvents } from "./useMcpAppEvents";
import { useMcpAppsPaneSync } from "./useMcpAppsPane";
import { useSessionIngestsSync } from "./useSessionIngests";
import { useSkillSync } from "./useSkillSync";
import { useStreamingTurnIds } from "./useStreamingTurnIds";
import { SubagentEventsProvider, useSubagentEvents } from "./useSubagentEvents";
import { useTurns } from "./useTurns";

const EMPTY_TOOL_CALL_IDS: ReadonlySet<string> = new Set();

interface ChatProps {
  agent: ReplayHttpAgent;
  conversationId: string;
  // Identity of the agent bound to this conversation — used to render the
  // assistant-message avatar consistently with the rest of the app.
  conversationAgent: PartialAgent | null;
  // True when /connect reported MESSAGE_IN_PROGRESS — attach to the server's
  // in-flight SSE stream on mount instead of starting a new run.
  resumeInFlight: boolean;
  // Skills available to the composer's slash-command picker; fetched at the
  // conversation level so a new conversation refreshes the list.
  skills: PartialSkill[];
  // Maps a DATASET skill's scope_id → its dataset display name, for the
  // "registered from" info tooltip in the skills picker.
  datasetNames: Record<string, string>;
  // Connectors granted to this conversation's agent and the names currently
  // enabled. Selection is owned by the conversation level and persisted there.
  connectors: AgentConnector[];
  enabledConnectorNames: string[];
  onToggleConnector: (connectorName: string) => void;
  // MCP-guidelines acceptance gate for the connectors picker.
  mcpGuidelines: McpGuidelines;
  // Per-turn connector allow-list sent as `forwardedProps.connectors`, or
  // `undefined` while loading so the backend can pick its default behavior.
  forwardedConnectors: string[] | undefined;
  starterPrompts: AgentStarterPrompt[];
  // Names of the dataset(s) backing this conversation's context, surfaced as a
  // folder indicator in the composer toolbar. A single entry today.
  contextDatasetNames?: string[];
  // First message typed on the New Session splash, sent once on mount so a
  // freshly created conversation opens straight into the user's query. The
  // conversation is created lazily at that submit — there's no empty shell.
  initialMessage?: string;
  // Artifact ids of attachments uploaded on the New Session splash, sent with
  // that first turn.
  initialAttachments?: string[];
  // True while the agent's connector grants or MCP-guidelines acceptance are
  // still loading. The lazy first turn waits for this to settle so it forwards
  // the splash's connector selection instead of racing ahead with the backend
  // default (or an empty allow-list).
  connectorsLoading?: boolean;
}

export const Chat = ({
  agent: rawAgent,
  conversationId,
  conversationAgent,
  starterPrompts,
  resumeInFlight,
  skills,
  datasetNames,
  connectors,
  enabledConnectorNames,
  onToggleConnector,
  mcpGuidelines,
  forwardedConnectors,
  contextDatasetNames,
  initialMessage,
  initialAttachments,
  connectorsLoading,
}: ChatProps) => {
  const { agent: agentBase } = useAgent(rawAgent);
  const agent = agentBase as ReplayHttpAgent;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showError } = useToast();

  const [showJump, setShowJump] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  // True while refetching /connect after a run ended with a dangling tool call.
  const [reconciling, setReconciling] = useState(false);
  // Previous isRunning, so the reconcile arms on the falling edge exactly once.
  const prevRunningRef = useRef(false);
  const liveTurnMeta = useStreamingTurnIds(agent);
  const subagentEvents = useSubagentEvents(agent);
  const mcpAppEntries = useMcpAppEvents(rawAgent);
  useMcpAppsPaneSync(rawAgent, agent.messages);
  useSessionIngestsSync(agent.messages);
  useSkillSync(rawAgent);
  useInitializationPanelSync(rawAgent);
  const turns = useTurns(agent.messages, agent.isRunning, liveTurnMeta);
  // useAgent re-renders on state changes, so this stays current.
  const contextBuffer = parseContextBuffer(agent.state);

  // Owns the scroll position (bottom pin, follow, deep link). `turns` is the
  // follow key — its identity changes as messages stream in. `releaseHold` ends
  // an active deep-link hold when the user takes over below.
  const { highlightedTurnId, releaseHold } = useAnchoredScroll(scrollRef, turns);

  const danglingCalls = useMemo(() => danglingToolCalls(turns), [turns]);
  const pausedIds = useMemo(() => hitlPauseIds(danglingCalls), [danglingCalls]);
  const settled = !agent.isRunning && !reconciling && !isCancelling;

  // A HITL pause blocks the composer until the user answers via its card —
  // free-form chat can't satisfy the deferred result the backend awaits. While
  // a run is in flight or being reconciled the local view isn't trustworthy
  // yet, so nothing is surfaced as awaiting until it settles.
  const awaitingToolCallIds = settled ? pausedIds : EMPTY_TOOL_CALL_IDS;
  const awaitingAnswer = awaitingToolCallIds.size > 0;

  const recomputeJump = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > BOTTOM_THRESHOLD);
  }, []);

  // Keep the jump-to-bottom button in sync as content grows; the scroll itself
  // is owned by `useAnchoredScroll`.
  useEffect(() => {
    recomputeJump();
  }, [turns, recomputeJump]);

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    releaseHold();
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // Tear down any in-flight SSE on unmount, regardless of how the run was
  // started. The replay attached here only fires when we mounted mid-run,
  // but the cleanup must also cover normal user-initiated runs started later
  // via handleSubmit — otherwise navigating away leaks the stream.
  // abortRun() is a no-op when nothing is running, so the unconditional
  // cleanup is safe in both paths.
  useEffect(() => {
    if (resumeInFlight) {
      agent.enableReplayForNextRun();
      void agent.runAgent();
    }
    return () => {
      agent.abortRun();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const handleSubmit = async (text: string, attachmentIds: string[] = []) => {
    // A new submit supersedes any in-flight cancel reconcile. Without this
    // the reconcile effect aborts when isRunning flips back to true and
    // never clears isCancelling, leaving the composer stuck on "Cancelling…"
    // for the whole new run.
    setIsCancelling(false);
    // The user is driving again; end any deep-link hold so the new turn follows.
    releaseHold();
    agent.addMessage({
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      // Extra field consumed by useTurns to render attachment cards on the
      // live user bubble. AG-UI doesn't re-validate added messages, so it
      // survives in memory; /connect re-supplies it from history on reload.
      ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
    } as Message);
    // forwardedProps.attachment_artifact_ids is the per-turn signal the
    // analysis-agent reads to announce the files to the model; connectors ride
    // the same channel.
    const forwardedProps: Record<string, unknown> = {};
    if (forwardedConnectors) forwardedProps.connectors = forwardedConnectors;
    if (attachmentIds.length > 0) forwardedProps.attachment_artifact_ids = attachmentIds;
    await agent.runAgent(Object.keys(forwardedProps).length > 0 ? { forwardedProps } : undefined);
  };

  // Send the message typed on the New Session splash as this conversation's
  // first turn. Same submit path as a hand-typed first message; the ref keeps
  // it to a single fire across re-renders. Skipped when resuming an in-flight
  // run, which owns the mount instead. Held until the connector grants settle
  // so `forwardedConnectors` carries the splash selection rather than racing
  // ahead while it's still undefined (which would forward the backend default).
  // The empty-state composer stays interactive during that wait, so bail if the
  // user has already submitted a turn — otherwise their message plus this one
  // would open the conversation with two turns for a single intent.
  const sentInitialRef = useRef(false);
  useEffect(() => {
    if (
      sentInitialRef.current ||
      (!initialMessage && !initialAttachments?.length) ||
      resumeInFlight ||
      connectorsLoading ||
      turns.length > 0
    ) {
      return;
    }
    sentInitialRef.current = true;
    void handleSubmit(initialMessage ?? "", initialAttachments);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once connectors settle
  }, [connectorsLoading]);

  // Answer to a paused HITL tool call (`ask_user_question`, `ask_for_approval`,
  // `confirm_ingest`). The deferred run ended with the call un-resulted; sending
  // the answer as a tool message and re-running lets the backend AGUIAdapter
  // rebuild DeferredToolResults and resume. Same supersede-the-cancel guard as
  // handleSubmit.
  //
  // A single response can pause on several HITL cards at once (e.g. two
  // `confirm_ingest` for two files). Resuming after only one is answered strands
  // the siblings — the backend backfills each as an interrupted tool-return that
  // can never become "approved", which is what leaves cards stuck. So stage each
  // answer as a tool message and only re-run once every paused HITL call in the
  // batch is answered.
  //
  // If the resume fails, roll every staged answer of the batch back out so the
  // cards return to their interactive state and the user can retry without
  // leaving orphaned tool-returns in the history. The rejection propagates so
  // the answered card can clear its "Submitting…" lock.
  const stagedAnswerIdsRef = useRef<string[]>([]);
  const handleAnswer = useCallback(
    async (toolCallId: string, answer: string) => {
      setIsCancelling(false);
      releaseHold();
      const messageId = crypto.randomUUID();
      agent.addMessage({ id: messageId, role: "tool", toolCallId, content: answer });
      stagedAnswerIdsRef.current.push(messageId);

      // `addMessage` mutates `agent.messages` synchronously, so this sees the
      // answer just staged. Other cards in this pause still awaiting → hold.
      if (unansweredHitlCallIds(agent.messages).size > 0) return;

      const batchIds = new Set(stagedAnswerIdsRef.current);
      stagedAnswerIdsRef.current = [];
      try {
        await agent.runAgent();
      } catch (err) {
        agent.setMessages(agent.messages.filter((m) => !batchIds.has(m.id)));
        console.error("Failed to resume after answer", err);
        showError("Couldn't send your answer. Try again in a moment.");
        throw err;
      }
    },
    [agent, showError, releaseHold]
  );

  // Refetch /connect and adopt the server's authoritative state — the SSE
  // stream alone can't distinguish paused / finished / cancelled / crashed /
  // dropped. MESSAGE_IN_PROGRESS means a run is still alive on the server, so
  // re-attach to its replay rather than freezing on a partial; READY means
  // quiescent, so trust the persisted messages.
  const reconcileFromConnect = useCallback(
    async (signal: AbortSignal) => {
      const data = await apiClient<ConversationConnect>(conversationConnectPath(conversationId), {
        signal,
      });
      if (signal.aborted) return;
      agent.setMessages(data.messages);
      if (data.status === "MESSAGE_IN_PROGRESS") {
        agent.enableReplayForNextRun();
        void agent.runAgent();
      }
    },
    [agent, conversationId]
  );

  // The analysis-agent closes the stream the same way on cancel as on normal
  // completion, so once a cancelled run's stream ends we reconcile to learn
  // whether the cancel actually landed.
  useEffect(() => {
    if (agent.isRunning || !isCancelling) return;
    const controller = new AbortController();
    reconcileFromConnect(controller.signal)
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        // Best-effort: log and clear so the user can retry or refresh.
        console.error("Failed to reconcile conversation after cancel", err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsCancelling(false);
      });
    return () => controller.abort();
  }, [agent, agent.isRunning, isCancelling, reconcileFromConnect]);

  // A run that ends with a dangling tool call either paused for input or was
  // interrupted (e.g. an LB idle-timeout during a slow generate_python_code).
  // Arm a reconcile on the run's falling edge so /connect resolves which —
  // re-attaching a still-live run or dropping an interrupted partial — instead
  // of sitting on a frozen view. The cancel path owns its own reconcile.
  useEffect(() => {
    const wasRunning = prevRunningRef.current;
    prevRunningRef.current = agent.isRunning;
    if (wasRunning && !agent.isRunning && !isCancelling && danglingCalls.length > 0) {
      setReconciling(true);
    }
  }, [agent.isRunning, isCancelling, danglingCalls]);

  useEffect(() => {
    if (!reconciling) return;
    const controller = new AbortController();
    reconcileFromConnect(controller.signal)
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to reconcile after run ended", err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setReconciling(false);
      });
    return () => controller.abort();
  }, [reconciling, reconcileFromConnect]);

  const handleCancel = useCallback(async () => {
    if (isCancelling) {
      return;
    }
    setIsCancelling(true);
    try {
      await cancelConversation(conversationId);
    } catch (err) {
      // Reset so the user can try again; the server-side run is still going.
      setIsCancelling(false);
      console.error("Failed to cancel conversation", err);
      showError("Couldn't stop generation. Try again in a moment.");
    }
  }, [conversationId, isCancelling, showError]);

  const isEmpty = turns.length === 0;
  const [starterPromptValue, setStarterPromptValue] = useState<string | undefined>(undefined);

  const composerProps = {
    isRunning: agent.isRunning,
    isCancelling,
    onCancel: handleCancel,
    onSubmit: handleSubmit,
    skills,
    datasetNames,
    connectors,
    enabledConnectorNames,
    onToggleConnector,
    mcpGuidelines,
    contextBuffer,
    conversationId,
    contextDatasetNames,
  };

  return (
    <ToolUIProvider value={TOOL_UI_REGISTRY}>
      <McpAppEntriesProvider value={mcpAppEntries}>
        <SubagentEventsProvider value={subagentEvents}>
          <div className="flex flex-1 flex-col overflow-hidden">
            {isEmpty ? (
              <EmptyConversationView
                agent={conversationAgent}
                starterPrompts={starterPrompts}
                onPromptClick={setStarterPromptValue}
              >
                <Composer {...composerProps} starterPromptValue={starterPromptValue} />
              </EmptyConversationView>
            ) : (
              <>
                <div
                  ref={scrollRef}
                  onScroll={recomputeJump}
                  className="flex-1 overflow-y-auto pt-4"
                >
                  <ol className="mx-auto flex max-w-[60rem] flex-col gap-4 px-6 pb-4" role="log">
                    <ConversationTurns
                      turns={turns}
                      conversationAgent={conversationAgent}
                      conversationId={conversationId}
                      isCancelling={isCancelling}
                      onAnswer={handleAnswer}
                      awaitingToolCallIds={awaitingToolCallIds}
                      highlightedTurnId={highlightedTurnId}
                    />
                  </ol>
                  <div
                    aria-hidden
                    className="pointer-events-none sticky bottom-0 h-8 bg-gradient-to-t from-background to-transparent"
                  />
                </div>

                <div className="relative px-4 pb-4">
                  {showJump && (
                    <button
                      type="button"
                      onClick={jumpToBottom}
                      aria-label={agent.isRunning ? "Jump to current response" : "Jump to bottom"}
                      className="absolute bottom-full left-1/2 z-20 mb-3 flex size-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground"
                    >
                      {agent.isRunning ? (
                        <TypingDots />
                      ) : (
                        <ChevronDown className="size-4" aria-hidden />
                      )}
                    </button>
                  )}
                  <Composer
                    {...composerProps}
                    disabled={awaitingAnswer || reconciling}
                    placeholder={
                      awaitingAnswer ? "Answer the question above to continue" : undefined
                    }
                  />
                </div>
              </>
            )}
          </div>
        </SubagentEventsProvider>
      </McpAppEntriesProvider>
    </ToolUIProvider>
  );
};

const TypingDots = () => (
  <span className="flex items-end gap-0.5" aria-hidden>
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "0ms" }}
    />
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "150ms" }}
    />
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "300ms" }}
    />
  </span>
);
